import React, { useMemo, useRef } from "react";
import Image from "next/image";
import PropTypes from "prop-types";

let API = "{host}/api/v1/get-pic";
const currentEnv = `${process.env.NEXT_PUBLIC_ENV_NAME}`;
// Cambio host de backend segun entorno
if (currentEnv !== "qa" || process.env.NEXT_PUBLIC_COL10IMAGES === "false") {
  // API = API.replace("{host}", "https://dev.ssr.col.movistar.es");
  API = API.replace("{host}", "https://ssr.col.movistar.es");
} else {
  API = "https://col10mgmt.tsm.inet";
}

const checkAbsoluteRoute = (src = "") => {
  return src && src.indexOf("http") !== -1;
};

const customLoader = (src) => {
  let finalSrc = src;
  let isAbsoluteRoute = checkAbsoluteRoute(src);

  // Miro si la ruta es absoluta para quedarnos solo con la url relativa o si empieza por //prod
  if (isAbsoluteRoute || finalSrc.indexOf("//") === 0) {
    if (finalSrc.indexOf("//") === 0) {
      finalSrc = `https:${finalSrc}`;
    }
    try {
      const urlObject = new URL(finalSrc);
      const rutaRelativa = urlObject.pathname;
      finalSrc = rutaRelativa;
    } catch (error) {
      // finalSrc = ''; lo dejo como idea el tener imagen de error
      console.log(error);
    }
  }

  return `${API}${finalSrc.trim()}`;
};

const getImageId = (entryPoint = "") => {
  let tmp = entryPoint.split(".");
  tmp = tmp[tmp.length - 1];
  tmp = tmp.split("-");
  return tmp[tmp.length - 1];
};

const Imagen = ({
  src,
  alt,
  fill,
  width,
  height,
  objectFit,
  objectPosition,
  className,
  custoObjectInfo,
  cyData,
  priority,
  onLoadCapture,
  onLoadingComplete,
}) => {
  if (!src) {
    return null;
  }

  const srcRef = useRef([]);

  useMemo(() => {
    srcRef.current.push(src);
  }, [src]);

  // Estilos para dimensionar la imagen
  const objectStyle = {
    objectFit: objectFit ? objectFit : undefined,
    objectPosition: objectPosition ? objectPosition : undefined,
    opacity: srcRef.current.length === 2 ? 0 : 1,
  };


  // Classname
  const finalClassName = `image${objectFit ? ` object-fit--${objectFit}` : ""}${
    objectPosition ? ` object-fit--${objectPosition}` : ""
  } ${className}`;

  // Priority
  let fetchPriorityAttribute = "high";
  if (!priority) {
    fetchPriorityAttribute = "none";
  }

  const onLoadingCompleteHandler = (e) => {
    e.style.opacity = 1;
    if (onLoadCapture) onLoadingComplete();
  };

  return (
    <>
      {custoObjectInfo && (
        <span
          className={`image image-${getImageId()} custo-editable`}
          onClick={() => custoObjectInfo.handler(custoObjectInfo.entryPoint)}
        >
          {custoObjectInfo.entryPoint}
        </span>
      )}
      <Image
        src={customLoader(src)}
        style={objectStyle}
        alt={alt}
        width={width}
        height={height}
        fill={fill}
        unoptimized
        className={finalClassName}
        data-cy={cyData}
        priority={priority}
        fetchPriority={fetchPriorityAttribute}
        onLoadCapture={onLoadCapture}
        onLoadingComplete={onLoadingCompleteHandler}
      />
    </>
  );
};

Imagen.propTypes = {
  /**
   * Url de la imagen.
   */
  src: PropTypes.string.isRequired,
  /**
   * Texto alternativo de la imagen.
   */
  alt: PropTypes.string.isRequired,
  /**
   * Ancho en píxeles de la imagen. Obligatorio excepto cuando se especifica la propiedad fill.
   */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Alto en píxeles de la imagen. Obligatorio excepto cuando se especifica la propiedad fill.
   */
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Estirará la imagen para ajustarse al contenedor padre en lugar de usar 'width' y 'height'. Cuando es 'true' es OBLIGATORIO asignarle al contenedor padre
   * uno de los siguientes estilos: position: "relative", position: "fixed" o position: "absolute".
   */
  fill: PropTypes.bool,
  /**
   * Establece cómo se debe redimensionar el contenido, para que se ajuste a su contenedor.
   */
  objectFit: PropTypes.oneOf([
    "fill",
    "contain",
    "cover",
    "none",
    "scale-down",
  ]),
  /**
   * Especifica la alineación del contenido del elemento dentro del elemento padre.
   */
  objectPosition: PropTypes.string,
  /**
   * Clase para añadirle estilos extras al icono.
   */
  className: PropTypes.string,
  /**
   * Para quitar loading-lazy.
   */
  priority: PropTypes.bool,
  /**
   * Objeto para la customización del icono en dev.
   */
  custoObjectInfo: PropTypes.shape({
    componentId: PropTypes.string,
    componentInstance: PropTypes.string,
    editFunction: PropTypes.func,
  }),
  cyData: PropTypes.string,
};

Imagen.defaultProps = {
  fill: false,
  custoObjectInfo: null,
  priority: false,
  className: "",
  cyData: "",
  onLoadCapture: null,
  onLoadingComplete: null,
};

export default Imagen;
